'use client';
import { useActionState, useEffect, useState } from 'react';
import { requestPasswordResetAction, getUserCsrfToken } from '@/app/actions/auth-actions';
import Image from 'next/image';
import { Mail } from 'lucide-react';

type ActionState = { error: string; success: boolean; message: string };

const initialState: ActionState = { error: '', success: false, message: '' };

async function requestResetAction(state: ActionState, formData: FormData): Promise<ActionState> {
  const res = await requestPasswordResetAction(state, formData);
  const next = res as { error?: string; success?: boolean; message?: string };
  return {
    error: next.error || '',
    success: Boolean(next.success),
    message: next.message || '',
  };
}

export default function RequestResetPage() {
  const [state, formAction, isPending] = useActionState(requestResetAction, initialState);
  const [csrfToken, setCsrfToken] = useState<string>('');

  useEffect(() => {
    getUserCsrfToken().then(setCsrfToken);
  }, []);

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-[var(--color-bg-base)] text-foreground relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-[var(--color-bg-base)] via-[var(--color-bg-base)] to-[var(--color-bg-base)] opacity-50 z-0 pointer-events-none" />

      <div className="relative z-10 w-full max-w-md p-8 bg-[var(--color-bg-surface)] backdrop-blur-xl border border-[var(--color-border)] rounded-2xl shadow-2xl">
        <div className="text-center mb-10">
          <div className="flex justify-center mb-6">
            <Image
              src="/logo/carsite.png"
              alt="CarSite Logo"
              width={200}
              height={54}
              className="h-12 w-auto object-contain light-mode-only"
              priority
            />
            <Image
              src="/logo/carsite-white.png"
              alt="CarSite Logo"
              width={200}
              height={54}
              className="h-12 w-auto object-contain dark-mode-only"
              priority
            />
          </div>
          <h1 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-primary to-primary/60">
            Forgot Password
          </h1>
          <p className="text-foreground/60 mt-2 text-sm">Enter your email to receive a reset link</p>
        </div>

        <form action={formAction} className="space-y-6">
          <input type="hidden" name="csrf_token" value={csrfToken} />
          <div className="space-y-2">
            <label htmlFor="email" className="text-xs font-medium text-foreground/60 ml-1 uppercase tracking-wider">Email</label>
            <div className="relative group">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-foreground/60 group-focus-within:text-primary transition-colors">
                <Mail size={18} />
              </div>
              <input
                id="email"
                type="email"
                name="email"
                autoComplete="email"
                required
                className="w-full pl-10 pr-4 py-3 bg-[var(--color-bg-base)] border border-[var(--color-border)] rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary/50 transition-all outline-none text-foreground placeholder-foreground/60"
                placeholder="you@example.com"
              />
            </div>
          </div>

          {state?.error && (
            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm text-center">
              {state.error}
            </div>
          )}
          {state?.success && state?.message && (
            <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm text-center">
              {state.message}
            </div>
          )}

          <button
            type="submit"
            disabled={isPending || !csrfToken}
            className="w-full py-3.5 px-4 bg-gradient-to-r from-primary to-primary/60 hover:from-primary hover:to-primary/60 text-white font-medium rounded-xl shadow-lg hover:shadow-emerald-500/25 transition-all transform hover:-translate-y-0.5 active:translate-y-0 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isPending ? 'Sending...' : 'Send Reset Link'}
          </button>
        </form>
      </div>
    </div>
  );
}
